import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { signOutUser } from '../../utils/auth';
import AvatarMenu from '../shared/AvatarMenu';

/**
 * AppNavBar — sticky top nav used on /create and /dashboard.
 * Props:
 *   pageTitle        – string shown next to the brand (e.g. "My Documents" or null for the create page)
 *   rightSlot        – optional JSX rendered between the "+ New" button and the user pill
 *   onNewApplication – optional callback for "+ New"; falls back to navigating to /create when absent
 *                      (e.g. on /dashboard, where there's no in-progress form to guard)
 */
const AppNavBar = ({ pageTitle, rightSlot, onNewApplication }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const credits = useSelector((state) => state.auth.credits);
  const isOnDashboard = location.pathname === '/dashboard';
  const handleSignOut = () => signOutUser({ dispatch, navigate, userId: user?.id });

  return (
    <div style={s.bar} className="app-nav-bar">
      {/* Left: logo + title */}
      <div style={s.left}>
        <button onClick={() => navigate('/')} style={s.logoBtn} title="ZYNACULAR">
          <span style={s.logoMark}>Z</span>
        </button>
        <div style={s.brandGroup}>
          <span style={s.brand}>ZYNACULAR</span>
          {pageTitle && (
            <>
              <span style={s.brandDivider}>/</span>
              <span style={s.pageTitle}>{pageTitle}</span>
            </>
          )}
        </div>
      </div>

      {/* Right: slot + My Documents + New Generation + user pill */}
      <div style={s.right} className="app-nav-right">
        {rightSlot}
        {!isOnDashboard && (
          <button onClick={() => navigate('/dashboard')} style={s.myDocsBtn} className="app-nav-my-docs">
            My Documents
          </button>
        )}
        <button
          onClick={() => onNewApplication ? onNewApplication() : navigate('/create')}
          style={s.newBtn}
          className="app-nav-new-btn"
        >
          + New
        </button>
        {user && (
          <AvatarMenu
            userEmail={user.email}
            credits={credits}
            onSignOut={handleSignOut}
          />
        )}
      </div>
    </div>
  );
};

const s = {
  bar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 32px',
    height: '64px',
    background: 'rgba(6, 9, 18, 0.92)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderBottom: '0.5px solid rgba(255, 255, 255, 0.07)',
    position: 'sticky',
    top: 0,
    zIndex: 50,
    gap: '16px',
    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    minWidth: 0,
  },
  logoBtn: {
    width: '36px',
    height: '36px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  logoMark: {
    fontFamily: "'Syne', sans-serif",
    fontWeight: 700,
    fontSize: '22px',
    letterSpacing: '0.05em',
    background: 'linear-gradient(135deg, #E8EEFF 0%, #2E6AFF 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  brand: {
    fontFamily: "'Outfit', system-ui, sans-serif",
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#E8EEFF',
  },
  brandGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  brandDivider: {
    color: '#3A4A6A',
    fontSize: '16px',
    fontWeight: 300,
    lineHeight: 1,
  },
  pageTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#E8EEFF',
    letterSpacing: '0.01em',
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexShrink: 0,
  },
  myDocsBtn: {
    padding: '8px 16px',
    background: 'transparent',
    border: '1px solid #1E2D4A',
    borderRadius: '8px',
    color: '#6B7FA8',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap',
  },
  newBtn: {
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #2E6AFF 0%, #1a4fd8 100%)',
    border: 'none',
    borderRadius: '8px',
    color: '#fff',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap',
  },
};

export default AppNavBar;
